import { useAppSelector } from "Hooks";
import {
  selectIsRevertById,
  selectOverrideById,
} from "../store/policyConfigSlice";
import { Override, Policy, PolicyOverride } from "../types";
import { useDerivedScanLevel } from "./index";

type UseDerivedPolicyOverrideProps = {
  hasCachedOverride: boolean;
  hasAnyOverride: boolean;
  hasUnsavedRevert: boolean;
  hasUnsavedOverride: boolean;
  unsavedOverride?: Override;
  policyOverrideState: PolicyOverride;
};

const useDerivedPolicyOverride = ({
  policy,
}: {
  policy: Policy;
}): UseDerivedPolicyOverrideProps => {
  const { derivedScanLevel } = useDerivedScanLevel();

  // override flags
  const unsavedOverride = useAppSelector((state) =>
    selectOverrideById(state, policy.id),
  );
  const hasUnsavedOverride = Boolean(unsavedOverride);

  const cachedOverride = policy.override;
  const hasCachedOverride = Boolean(cachedOverride);

  const hasAnyOverride = hasUnsavedOverride || hasCachedOverride;

  // revert flags
  const hasUnsavedRevert: boolean = useAppSelector((state) =>
    selectIsRevertById(state, policy.id),
  );

  // produce an object that represents the current display state of a policy by taking into account
  // - whether it has overrides in the cache data
  // - whether it has an unsaved revert that would negate that override
  // - whether it has an unsaved override that would supplant all of that
  // This allows us to show the current, potentially as yet unsaved state of a given policy in the table
  //

  let policyOverrideState: PolicyOverride = {
    ...policy.inherited,
    author: "",
    reason: "",
    timestamp: "",
  };

  // cached override in place, revert has been actioned but not saved
  if (hasUnsavedRevert)
    policyOverrideState = {
      ...policyOverrideState,
      ...cachedOverride,
      ...policy.inherited,
    };
  // new or updated override has been actioned but not saved
  else if (hasUnsavedOverride)
    policyOverrideState = {
      ...policyOverrideState,
      ...cachedOverride,
      ...unsavedOverride,
    };
  // cached override in place with no revert or override actioned
  else if (hasCachedOverride)
    policyOverrideState = {
      ...policyOverrideState,
      ...policy.inherited,
      ...policy.override,
    };
  // if levels are set, and inherited policy is not user override,
  // blocker is determined based on level
  else if (
    derivedScanLevel !== null &&
    derivedScanLevel !== 0 &&
    policy.rl_level !== 0 &&
    !policy.inherited.is_user_override
  ) {
    policyOverrideState = {
      ...policyOverrideState,
      enabled: true,
      blocker: policy.rl_level <= derivedScanLevel,
    };
  }
  // nothing in place or "don't use levels", just the inherited values
  else
    policyOverrideState = {
      ...policyOverrideState,
      ...policy.inherited,
    };

  return {
    hasCachedOverride,
    hasUnsavedOverride,
    hasAnyOverride,
    hasUnsavedRevert,
    policyOverrideState,
  };
};

export default useDerivedPolicyOverride;
